import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Clock, Trophy, Gamepad2, ChevronRight, Calendar,
  ArrowUpRight, ArrowDownRight, Filter, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MatchHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API}/matches/my-matches`, { withCredentials: true });
      setMatches(response.data);
    } catch (error) {
      console.error('Error fetching matches:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'open': return { color: '#00F0FF', bg: 'bg-[#00F0FF]/10', text: 'Open' };
      case 'in_progress': return { color: '#FFB800', bg: 'bg-[#FFB800]/10', text: 'In Progress' };
      case 'awaiting_proof': return { color: '#7000FF', bg: 'bg-[#7000FF]/10', text: 'Awaiting Proof' };
      case 'disputed': return { color: '#FF2E2E', bg: 'bg-[#FF2E2E]/10', text: 'Disputed' };
      case 'completed': return { color: '#00FF94', bg: 'bg-[#00FF94]/10', text: 'Completed' };
      case 'cancelled': return { color: '#FFFFFF', bg: 'bg-white/10', text: 'Cancelled' };
      default: return { color: '#FFFFFF', bg: 'bg-white/10', text: status };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredMatches = matches.filter(match => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'active') return ['open', 'in_progress', 'awaiting_proof'].includes(match.status);
    if (statusFilter === 'completed') return match.status === 'completed';
    if (statusFilter === 'disputed') return match.status === 'disputed';
    return true;
  });

  const completedMatches = matches.filter(m => m.status === 'completed');
  const wonMatches = completedMatches.filter(m => m.winner_id === user?.user_id);
  const winRate = completedMatches.length > 0 
    ? Math.round((wonMatches.length / completedMatches.length) * 100) 
    : 0;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="history-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">Match History</h1>
          <p className="text-white/50">Your past matches and statistics</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="glass-card rounded-xl p-5"
          >
            <p className="text-white/50 text-sm mb-1">Total Matches</p>
            <p className="font-mono text-2xl font-bold text-white">{matches.length}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="glass-card rounded-xl p-5"
          >
            <p className="text-white/50 text-sm mb-1">Wins</p>
            <p className="font-mono text-2xl font-bold text-[#00FF94]">{user?.wins || 0}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-xl p-5"
          >
            <p className="text-white/50 text-sm mb-1">Losses</p>
            <p className="font-mono text-2xl font-bold text-[#FF2E2E]">{user?.losses || 0}</p>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card rounded-xl p-5"
          >
            <p className="text-white/50 text-sm mb-1">Win Rate</p>
            <p className="font-mono text-2xl font-bold text-[#00F0FF]">{winRate}%</p>
          </motion.div>
        </div>

        {/* Filter */}
        <div className="glass-card rounded-xl p-4 mb-6">
          <div className="flex items-center gap-4">
            <Filter className="w-4 h-4 text-white/50" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-white/5 border-white/10" data-testid="status-filter">
                <SelectValue placeholder="All Matches" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0B] border-white/10">
                <SelectItem value="all">All Matches</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="disputed">Disputed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Match List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : filteredMatches.length === 0 ? (
          <div className="glass-card rounded-xl p-12 text-center">
            <Clock className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <h2 className="font-heading font-semibold text-xl mb-2">No Matches Found</h2>
            <p className="text-white/50 mb-6">
              {statusFilter !== 'all' 
                ? 'No matches with the selected filter'
                : 'Start competing to build your history!'}
            </p>
            <Button 
              onClick={() => navigate('/create-match')}
              className="btn-primary clip-slant"
              data-testid="history-create-match-btn"
            >
              Create a Match
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredMatches.map((match, i) => {
              const statusConfig = getStatusConfig(match.status);
              const isWinner = match.winner_id === user?.user_id;
              const isCreator = match.creator_id === user?.user_id;
              const opponentName = isCreator ? match.opponent_name : match.creator_name;
              
              return (
                <motion.div
                  key={match.match_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  onClick={() => navigate(`/match/${match.match_id}`)}
                  className="glass-card rounded-xl p-5 cursor-pointer hover:border-white/20 transition-all"
                  data-testid={`history-match-${match.match_id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        match.status === 'completed'
                          ? isWinner ? 'bg-[#00FF94]/20' : 'bg-[#FF2E2E]/20'
                          : 'bg-[#7000FF]/20'
                      }`}>
                        {match.status === 'completed' ? (
                          isWinner ? (
                            <ArrowDownRight className="w-6 h-6 text-[#00FF94]" />
                          ) : (
                            <ArrowUpRight className="w-6 h-6 text-[#FF2E2E]" />
                          )
                        ) : (
                          <Gamepad2 className="w-6 h-6 text-[#7000FF]" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-heading font-semibold">{match.game_name}</h3>
                        <p className="text-white/50 text-sm">
                          {opponentName 
                            ? `vs ${opponentName}`
                            : 'Waiting for opponent'
                          }
                        </p>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-xs text-white/40">
                            <Calendar className="w-3 h-3" />
                            {formatDate(match.created_at)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-mono font-bold text-lg text-[#CCFF00]">
                          {(match.wager_amount * 100).toLocaleString()} credits
                        </p>
                        {match.status === 'completed' && (
                          <p className={`text-sm font-mono ${isWinner ? 'text-[#00FF94]' : 'text-[#FF2E2E]'}`}>
                            {isWinner 
                              ? `+$${match.prize_amount?.toFixed(2) || ((match.wager_amount * 2) - (match.platform_fee * 2)).toFixed(2)}`
                              : `-${(match.wager_amount * 100).toLocaleString()} credits`
                            }
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span 
                          className={`text-xs px-3 py-1 rounded-full ${statusConfig.bg}`}
                          style={{ color: statusConfig.color }}
                        >
                          {statusConfig.text}
                        </span>
                        {match.status === 'completed' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#00F0FF] p-1"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/match/${match.match_id}/receipt`);
                            }}
                            data-testid={`receipt-btn-${match.match_id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                        )}
                        <ChevronRight className="w-4 h-4 text-white/30" />
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MatchHistory;
